import React from 'react';
import { Dialog, DialogContent } from './dialog';
import { cn } from '../../lib/utils';

const SIZE = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
};

/** True on >= sm viewports. One matchMedia instead of one per component. */
export function useIsDesktop() {
  // Lazy init so the first paint already has the right variant — otherwise a
  // desktop dialog flashes as a bottom sheet before snapping to a modal.
  const [isDesktop, setIsDesktop] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches,
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return isDesktop;
}

/**
 * The app's standard responsive dialog: a bottom sheet that slides up on mobile,
 * a centered modal that zooms in on desktop. Defined once so every sheet
 * (device details, scene builder, pickers, automations…) behaves identically
 * and there's a single place to change the behavior.
 *
 * Children are placed directly inside the content (already `flex flex-col`,
 * `overflow-hidden`, no padding) — compose a header, a `flex-1 min-h-0
 * overflow-y-auto` body, and an optional footer.
 */
export function ResponsiveSheet({ open, onClose, size = 'lg', className, children }) {
  const isDesktop = useIsDesktop();
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose?.(); }}>
      <DialogContent
        variant={isDesktop ? 'modal' : 'sheet'}
        className={cn(
          'overflow-hidden p-0 flex flex-col',
          isDesktop && cn(SIZE[size] || SIZE.lg, 'max-h-[85vh] rounded-2xl'),
          className,
        )}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
}

export default ResponsiveSheet;
